import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { combineLatest, ConnectableObservable, Observable } from 'rxjs';
import { map, publishLast, publishReplay } from 'rxjs/operators';

import { BgxLocationService } from '@bgx/services/location';

import { ICurrentNodes, INavigationNode, INavigationResponse, INavigationViews } from './navigation.interfaces';

export const navigationPath: string = 'assets/navigation.json';

@Injectable()
export class BgxNavigationService {
  navigationViews: Observable<INavigationViews>;
  currentNodes: Observable<ICurrentNodes>;

  constructor(private _http: HttpClient, private _location: BgxLocationService) {
    const navigationInfo: Observable<INavigationResponse> = this.fetchNavigationInfo();
    this.navigationViews = this.getNavigationViews(navigationInfo);
    this.currentNodes = this.getCurrentNodes(this.navigationViews);
  }

  private fetchNavigationInfo(): Observable<INavigationResponse> {
    const navigationInfo: Observable<any> = this._http.get<INavigationResponse>(navigationPath).pipe(publishLast());
    (navigationInfo as ConnectableObservable<INavigationResponse>).connect();

    return navigationInfo;
  }

  private getNavigationViews(navigationInfo: Observable<INavigationResponse>): Observable<INavigationViews> {
    const navigationViews: Observable<any> = navigationInfo.pipe(
      map((response: INavigationResponse) => {
        const views: INavigationResponse = Object.assign({}, response);
        Object.keys(views).forEach((key: string) => {
          if (key[0] === '_') {
            delete views[key];
          }
        });
        return views as INavigationViews;
      }),
      publishLast(),
    );
    (navigationViews as ConnectableObservable<INavigationViews>).connect();
    return navigationViews;
  }

  private getCurrentNodes(navigationViews: Observable<INavigationViews>): Observable<ICurrentNodes> {
    const currentNodes: Observable<ICurrentNodes> = combineLatest([
      navigationViews.pipe(map((views: INavigationViews) => this.computeUrlToNavNodesMap(views))),
      this._location.currentPath,
    ]).pipe(
      map((result: any) => ({ navMap: result[0], url: result[1] })),
      map((result: any) => {
        return result.navMap.get(result.url) || { '': { view: '', url: result.url, nodes: [] } };
      }),
      publishReplay(1),
    );
    (currentNodes as ConnectableObservable<ICurrentNodes>).connect();
    return currentNodes;
  }

  /**
   * Compute a mapping from URL to an array of nodes, where the first node in the array
   * is the one that matches the URL and the rest are the ancestors of that node.
   *
   * @param navigation - A collection of navigation nodes that are to be mapped
   */
  private computeUrlToNavNodesMap(navigation: INavigationViews): Map<string, ICurrentNodes> {
    const navMap: Map<string, ICurrentNodes> = new Map<string, ICurrentNodes>();
    Object.keys(navigation).forEach((view: string) =>
      navigation[view].forEach((node: INavigationNode) => this.walkNodes(view, navMap, node)),
    );
    return navMap;
  }

  /**
   * Add tooltip to node if it doesn't have one and have title.
   * If don't want tooltip, specify `"tooltip": ""` in navigation.json
   */
  private ensureHasTooltip(node: INavigationNode): void {
    const title: string = node.title;
    const tooltip: string = node.tooltip;
    if (tooltip === undefined && title) {
      // add period if no trailing punctuation
      node.tooltip = title + (/[a-zA-Z0-9]$/.test(title) ? '.' : '');
    }
  }

  private walkNodes(
    view: string,
    navMap: Map<string, ICurrentNodes>,
    node: INavigationNode,
    ancestors: INavigationNode[] = [],
  ): void {
    const nodes: INavigationNode[] = [node, ...ancestors];
    const url: string = node.url;
    this.ensureHasTooltip(node);

    // only map to this node if it has a url
    if (url) {
      // Strip off trailing slashes from nodes in the navMap - they are not relevant to matching
      const cleanedUrl: string = url.replace(/\/$/, '');
      if (!navMap.has(cleanedUrl)) {
        navMap.set(cleanedUrl, {});
      }
      const navMapItem: ICurrentNodes = navMap.get(cleanedUrl);
      navMapItem[view] = { url, view, nodes };
    }

    if (node.children) {
      node.children.forEach((child: INavigationNode) => this.walkNodes(view, navMap, child, nodes));
    }
  }
}
